import { useCallback, useEffect, useState } from 'react';
import { Loader2, ShieldAlert } from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../lib/contextoAuth';
import { useAvisos } from '../lib/contextoAvisos';
import Ayuda from '../ui/Ayuda';
import NotaExplicativa from '../ui/NotaExplicativa';

/* =====================================================================
   Cuarentena · TC-22
   =====================================================================
   Lo que una terminal subió y no cuadró (repisa que ya no es la suya,
   producto que no está, cantidad imposible). Se aplica al inventario o
   se descarta; las dos dejan rastro, y hace falta el permiso.
   ===================================================================== */
export default function Cuarentena() {
  const avisos = useAvisos();
  const { puede } = useAuth();
  const [tick, setTick] = useState(0);
  const recargar = useCallback(() => setTick((t) => t + 1), []);
  const [lista, setLista] = useState(null);
  const [error, setError] = useState('');
  const [nota, setNota] = useState({});
  const [verResueltos, setVerResueltos] = useState(false);

  useEffect(() => {
    let vivo = true;
    api.get('/cuarentena').then((l) => { if (vivo) { setLista(l); setError(''); } }).catch((e) => { if (vivo) setError(e.message); });
    return () => { vivo = false; };
  }, [tick]);

  async function resolver(id, resolucion) {
    try {
      await api.post(`/cuarentena/${id}/resolver`, { resolucion, nota: nota[id] || null });
      avisos.exito(resolucion === 'APLICADO' ? 'Aplicado al inventario, con tu nombre.' : 'Descartado. Queda escrito que llegó y que se descartó.');
      recargar();
    } catch (e) { avisos.error(e.message); }
  }

  if (error && !lista) return <p className="p-4 text-sm text-red-700">{error}</p>;
  if (!lista) {
    return (
      <p className="flex items-center gap-2 p-4 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Leyendo la cuarentena…
      </p>
    );
  }
  const puedeResolver = puede ? puede('cuarentena.resolver') : false;
  const pendientes = lista.filter((r) => !r.resuelto_en);
  const resueltos = lista.filter((r) => r.resuelto_en);
  return (
    <div className="flex max-w-5xl flex-col gap-5 p-4">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Lo retenido en cuarentena</h1>
        <p className="text-gray-600">Lo que una terminal contó y no cuadró. Aquí se decide.</p>
        <Ayuda id="terminal.cuarentena.pantalla" />
      </header>
      {pendientes.length === 0 ? (
        <NotaExplicativa titulo="Nada en cuarentena">Todo lo que subieron las terminales cuadró y ya está en el inventario.</NotaExplicativa>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200">{pendientes.map((r) => (
          <li key={r.id} className="p-3 text-sm">
            <div className="flex flex-wrap items-center gap-x-3">
              <ShieldAlert className="h-4 w-4 text-amber-600" aria-hidden="true" />
              <span className="text-gray-500">{String(r.ocurrido_en || r.recibido_en).slice(0, 16).replace('T', ' ')}</span>
              <span className="font-mono">{r.ubicacion || '—'}</span>
              <span className="text-gray-600">{r.aparato}{r.operador ? ` · ${r.operador}` : ''}</span>
              <span className="font-medium">{r.producto || r.sku || '—'}</span>
              {r.delta != null && <span className="tabular-nums">{r.delta > 0 ? '+' : ''}{r.delta}</span>}
            </div>
            <p className="mt-1 text-amber-800">{r.motivo}</p>
            {puedeResolver ? (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <input value={nota[r.id] || ''} onChange={(e) => setNota({ ...nota, [r.id]: e.target.value })} placeholder="Nota (opcional)" className="rounded-lg border border-gray-300 p-1.5 text-sm" />
                <button type="button" onClick={() => resolver(r.id, 'APLICADO')} className="rounded-xl bg-[var(--color-marca)] px-3 py-1.5 text-xs font-semibold text-white">Aplicar al inventario</button>
                <button type="button" onClick={() => { if (window.confirm('Descartar deja escrito que llegó y no se aplicó. ¿Seguro?')) resolver(r.id, 'DESCARTADO'); }} className="rounded-xl border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700">Descartar</button>
              </div>
            ) : <p className="mt-1 text-xs text-gray-500">Para resolverlo hace falta el permiso «Resolver lo que quedó en cuarentena»; pídeselo a un administrador.</p>}
          </li>))}</ul>
      )}
      {resueltos.length > 0 && (
        <div>
          <button type="button" onClick={() => setVerResueltos((v) => !v)} className="text-sm font-semibold text-[var(--color-marca)]">{verResueltos ? 'Ocultar' : 'Ver'} los {resueltos.length} ya resueltos</button>
          {verResueltos && <ul className="mt-2 divide-y divide-gray-100 rounded-xl border border-gray-200 text-sm text-gray-600">{resueltos.map((r) => (
            <li key={r.id} className="flex flex-wrap gap-x-3 p-2"><span>{String(r.resuelto_en).slice(0, 16).replace('T', ' ')}</span><span className={r.resolucion === 'APLICADO' ? 'text-green-800' : ''}>{r.resolucion}</span><span>{r.producto || r.sku}</span><span>{r.resuelto_por_nombre}</span>{r.nota && <span className="italic">{r.nota}</span>}</li>))}</ul>}
        </div>
      )}
    </div>
  );
}
